import React, { useState } from "react";
import { Locale } from "@kaltura/mediaspace-shared-types";
import { postKmsData } from "@kaltura/mediaspace-shared-utils";
import { ConfirmationModal } from "@kaltura/mediaspace-shared-ui";
import { LocaleMenu } from "../header";

/**
 * "are you sure" prompt to show before changing the locale.
 */
export interface MoeLocaleChangeConfirmation {
    title: string;
    text: string;
    confirmLabel: string;
}

export interface MoeLocaleMenuProps {
    locales: Locale[];
    currentLocale: Locale;
    /**
     * url for saving the language the user chose
     */
    setLocaleUrl: string;
    /**
     * when set, the user is asked to confirm the change before it is saved
     */
    localeChangeConfirmation?: MoeLocaleChangeConfirmation;
    disableScrollLock?: boolean;
}

/**
 * MOE locale menu - saves the chosen language on the user (optionally after the user's confirmation),
 * rather than in the kms-locale cookie as the core locale menu does.
 */
export function MoeLocaleMenu({
    locales,
    currentLocale,
    setLocaleUrl,
    localeChangeConfirmation,
    disableScrollLock,
}: MoeLocaleMenuProps) {
    // the locale the user chose, pending the user's confirmation
    const [pendingLocale, setPendingLocale] = useState<Locale>();
    const [isSavingLocale, setIsSavingLocale] = useState(false);

    const handleSaveFailed = (locale: Locale, error?: unknown) => {
        console.error("failed to set locale " + locale.id, error);
        setIsSavingLocale(false);
        setPendingLocale(undefined);
    };

    // persist the chosen language on the user, then reload the page (without a "lang" param) to render it in that language
    const saveLocale = async (locale: Locale) => {
        setIsSavingLocale(true);
        try {
            const result = await postKmsData(setLocaleUrl, { locale: locale.id });
            if (!result?.success) {
                handleSaveFailed(locale);
                return;
            }
        }
        catch (e) {
            handleSaveFailed(locale, e);
            return;
        }
        // keep the "saving" state until the page reloads
        const url = new URL(window.location.href);
        url.searchParams.delete("lang");
        window.location.href = url.toString();
    };

    const handleLocaleChange = (locale: Locale) => {
        if (locale.id === currentLocale.id) {
            return;
        }
        if (localeChangeConfirmation) {
            setPendingLocale(locale);
            return;
        }
        saveLocale(locale);
    };

    return (
        <>
            <LocaleMenu
                locales={locales}
                onLocaleChange={handleLocaleChange}
                disableScrollLock={disableScrollLock}
                currentLocale={currentLocale}
                buttonColor={"secondary"}
            />
            {localeChangeConfirmation && (
                <ConfirmationModal
                    open={!!pendingLocale}
                    title={localeChangeConfirmation.title}
                    text={localeChangeConfirmation.text}
                    yesButtonLabel={localeChangeConfirmation.confirmLabel}
                    processing={isSavingLocale}
                    yesCallback={() => pendingLocale && saveLocale(pendingLocale)}
                    noCallback={() => setPendingLocale(undefined)}
                />
            )}
        </>
    );
}
